/**
 * Table-driven windowed-sinc interpolation — the libsamplerate approach.
 *
 * A Kaiser-windowed sinc is evaluated at `phases` fractional offsets ahead of
 * time, so each output sample is one FIR of `taps` multiplies regardless of the
 * kernel's length. That's why it beats a Farrow of the same length on both
 * quality and cost: Farrow pays `taps × (order + 1)` multiplies to compute
 * coefficients it could have looked up.
 *
 * The table is generated from a closed-form expression rather than fitted, so
 * there are no designed coefficients to get wrong — the only knobs are length,
 * phase count, and the Kaiser beta.
 *
 * `cutoff` below 1 narrows the passband, which is what a resampler needs when
 * decimating. We don't use it: for af's rates in a 48 kHz context, speeding up
 * to 2x never pushes content past the output Nyquist, so there is nothing to
 * alias and nothing to filter out.
 */

/** Modified Bessel function of the first kind, order 0. */
function besselI0(x) {
  let sum = 1;
  let term = 1;
  for (let k = 1; k < 50; k++) {
    term *= (x / (2 * k)) ** 2;
    sum += term;
    if (term < 1e-17 * sum) break;
  }
  return sum;
}

export function buildSincTable(taps, phases, beta = 8.6, cutoff = 1) {
  const table = new Float32Array(phases * taps);
  const half = taps / 2;
  const normalisation = besselI0(beta);

  for (let p = 0; p < phases; p++) {
    const mu = p / phases;
    let sum = 0;

    for (let k = 0; k < taps; k++) {
      const t = k - (half - 1) - mu;
      const sinc = t === 0 ? cutoff : Math.sin(Math.PI * cutoff * t) / (Math.PI * t);

      const position = t / half;
      const window =
        Math.abs(position) >= 1
          ? 0
          : besselI0(beta * Math.sqrt(1 - position * position)) / normalisation;

      const value = sinc * window;
      table[p * taps + k] = value;
      sum += value;
    }

    // Normalise each phase to unity gain. Without this the output amplitude
    // wobbles at the phase rate, which is an audible whine at some speeds.
    for (let k = 0; k < taps; k++) table[p * taps + k] /= sum;
  }

  return table;
}

export function readWindowedSinc(w, i, mu, table, params) {
  const taps = params.taps;
  const base = ((mu * params.phases) | 0) * taps;

  let acc = 0;
  for (let k = 0; k < taps; k++) acc += table[base + k] * w[k];
  return acc;
}

export function createWindowedSinc(taps, { phases = 512, beta = 8.6, notes } = {}) {
  const half = taps / 2;

  return {
    id: `sinc-${taps}`,
    label: `Windowed sinc (${taps}-tap)`,
    kernel: "sinc",
    taps,
    before: half - 1,
    after: half,
    notes:
      notes ?? `Kaiser-windowed, ${phases} phases. ${taps} multiplies per sample plus a table.`,
    buildTable: () => buildSincTable(taps, phases, beta),
    params: { taps, phases },
  };
}
